import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import { Send, LogIn } from "lucide-react";
import ForumEditorToolbar from "@/components/ForumEditorToolbar";

export default function ForumNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    category: params.get("category") || "general",
    title: "",
    body: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    client.get("/forum/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);
  if (loading) {
    return <div className="text-center py-32 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow" data-testid="forum-new-loading">// verifying clearance...</div>;
  }
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="forum-new-signin">
        <h1 className="font-display text-3xl text-[var(--t-cyan)] uppercase mb-4">Sign In to Post</h1>
        <p className="text-[var(--t-text-dim)] mb-6">Only authenticated Citizens can open transmissions.</p>
        <button onClick={startLogin} className="btn-primary" data-testid="forum-new-signin-btn">
          <LogIn className="w-4 h-4" /> Sign in with Discord
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.title.trim().length < 3) { setError("Title must be at least 3 characters."); return; }
    if (form.body.trim().length < 5) { setError("Body must be at least 5 characters."); return; }
    setBusy(true);
    try {
      const r = await client.post("/forum/posts", form);
      navigate(`/forum/${r.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to publish.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="forum-new-page">
      <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Open Channel</span>
      <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase mb-3 leading-tight">New Transmission</h1>
      <p className="text-sm md:text-base text-[var(--t-text-dim)] mb-7 md:mb-8 max-w-2xl">
        Speak your mind, Citizen. Posts go live immediately. Admins may promote the best ones to the official codex.
      </p>

      <form onSubmit={submit} className="panel panel-glow p-5 md:p-7 space-y-5" data-testid="forum-new-form">
        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Channel *</label>
          <select className="field" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="forum-new-category">
            {categories.map(g => (
              <optgroup key={g.slug} label={g.name}>
                {g.channels.map(c => (
                  <option key={c.slug} value={c.slug}>{c.name} — {c.description}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Title *</label>
          <input
            className="field"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Best Tropical farm rotation for early game"
            maxLength={200}
            data-testid="forum-new-title"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Body *</label>
          <ForumEditorToolbar
            textareaRef={bodyRef}
            value={form.body}
            onChange={(v) => setForm({ ...form, body: v })}
            testidPrefix="new-post"
          />
          <textarea
            ref={bodyRef}
            className="field"
            rows={10}
            value={form.body}
            onChange={e => setForm({ ...form, body: e.target.value })}
            placeholder="Share your strategy, lore, bug or idea..."
            maxLength={20000}
            data-testid="forum-new-body"
          />
          <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.18em] mt-1.5">{form.body.length}/20000</div>
        </div>

        {error && <div className="text-sm text-[var(--t-red)] font-mono" data-testid="forum-new-error">{error}</div>}

        <div className="flex justify-between items-center flex-wrap gap-3">
          <span className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.18em] uppercase">signed in as {user.name || user.email}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => navigate("/forum")} className="btn-ghost !text-xs" data-testid="forum-new-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary !text-xs" data-testid="forum-new-submit">
              <Send className="w-4 h-4" /> {busy ? "Publishing..." : "Publish"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
